import React from 'react'
import { Box } from '@mui/material'
import type { CarRunnerMiniGameProps, GamePhase, UiState } from './types'
import { rectsOverlap, readBestScore, writeBestScore, clamp } from './helpers'
import { PHYSICS_CONFIG } from './config'
import { renderScene } from './rendering'
import { createScenery } from './background/scenery'
import { computeNextSpawnDelay, spawnGroup } from './spawning'
import { createInitialState } from './state'

const CarRunnerMiniGame = ({
  running = true,
  width = 360,
  height = 140,
}: CarRunnerMiniGameProps) => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)
  const rafRef = React.useRef<number | null>(null)
  const [phase, setPhase] = React.useState<GamePhase>('ready')
  const uiRef = React.useRef<UiState>({ startBtn: { x: 0, y: 0, w: 0, h: 0 } })
  const stateRef = React.useRef(createInitialState())
  const jumpChargeRef = React.useRef({ active: false, startTime: 0 })
  const jumpAllowedRef = React.useRef(true)

  const canSpawnNow = React.useCallback(() => {
    const s = stateRef.current
    if (s.obstacles.length === 0) return true
    const last = s.obstacles[s.obstacles.length - 1]
    const safeLine = width - s.safeAfterLastObstaclePx
    return last.x + last.w < safeLine
  }, [width])

  const resetWorld = React.useCallback(() => {
    const s = stateRef.current
    const base = createInitialState()
    Object.assign(s, base)
    s.groundY = height - PHYSICS_CONFIG.groundPadding
    s.car.y = s.groundY - s.car.h
    s.best = readBestScore()
    jumpChargeRef.current = { active: false, startTime: 0 }
    jumpAllowedRef.current = true
    s.scenery = createScenery(s.seed, width, height)
  }, [height, width])

  const startGame = React.useCallback(() => {
    if (!running) return
    resetWorld()
    setPhase('playing')
  }, [resetWorld, running])

  const crash = React.useCallback(() => {
    const s = stateRef.current
    s.lastScoreAtCrash = s.score
    const nextBest = Math.max(s.best, s.score)
    s.best = nextBest
    writeBestScore(nextBest)
    setPhase('crashed')
  }, [])

  const startJump = React.useCallback((allowHold: boolean) => {
    if (!jumpAllowedRef.current) return
    if (allowHold && !jumpAllowedRef.current) return
    const s = stateRef.current
    if (!s.car.onGround) return
    if (allowHold) jumpAllowedRef.current = false
    s.car.onGround = false
    s.car.vy = s.jumpVelocity
    if (allowHold) {
      jumpChargeRef.current = { active: true, startTime: performance.now() }
    } else {
      jumpChargeRef.current = { active: false, startTime: 0 }
    }
  }, [])

  const releaseJumpCharge = React.useCallback(() => {
    jumpChargeRef.current.active = false
  }, [])

  const draw = React.useCallback(() => {
    if (!canvasRef.current) return
    const ctx = canvasRef.current.getContext('2d')
    if (!ctx) return
    renderScene(ctx, width, height, phase, uiRef, stateRef)
  }, [height, phase, width])

  const step = React.useCallback(
    (t: number) => {
      const s = stateRef.current
      if (!s.tPrev) s.tPrev = t
      const dt = Math.min(0.033, (t - s.tPrev) / 1000)
      s.tPrev = t

      if (!s.groundY) {
        s.groundY = height - PHYSICS_CONFIG.groundPadding
        s.car.y = s.groundY - s.car.h
      }

      s.elapsed += dt

      if (jumpChargeRef.current.active) {
        const heldFor = performance.now() - jumpChargeRef.current.startTime
        if (heldFor >= PHYSICS_CONFIG.maxJumpHoldMs) {
          releaseJumpCharge()
        }
      }

      if (running && phase === 'playing') {
        s.speed += s.speedInc * dt
        s.safeAfterLastObstaclePx = clamp(
          PHYSICS_CONFIG.minSafeDistancePx + (s.speed - 245) * 0.12,
          PHYSICS_CONFIG.minSafeDistancePx,
          270,
        )

        s.spawnCooldown += dt
        if (s.nextSpawnIn <= 0) {
          s.nextSpawnIn = computeNextSpawnDelay(s)
        }

        if (s.spawnCooldown >= s.nextSpawnIn && canSpawnNow()) {
          s.spawnCooldown = 0
          s.nextSpawnIn = computeNextSpawnDelay(s)
          spawnGroup(s, width)
        }

        const effectiveGravity =
          jumpChargeRef.current.active && s.car.vy >= 0
            ? PHYSICS_CONFIG.gravityHoldDescend
            : PHYSICS_CONFIG.gravity
        s.car.vy += effectiveGravity * dt
        s.car.y += s.car.vy * dt

        if (s.car.y < 2) {
          s.car.y = 2
          if (s.car.vy < 0) s.car.vy = 0
        }

        const groundCarY = s.groundY - s.car.h
        if (s.car.y >= groundCarY) {
          s.car.y = groundCarY
          s.car.vy = 0
          s.car.onGround = true
          jumpChargeRef.current.active = false
        }

        s.obstacles = s.obstacles
          .map((o) => ({ ...o, x: o.x - s.speed * dt }))
          .filter((o) => o.x + o.w > -20)

        const carHitbox = {
          x: s.car.x + 6,
          y: s.car.y + 4,
          w: s.car.w - 12,
          h: s.car.h - 3,
        }

        const collided = s.obstacles.some((o) => {
          const oy = s.groundY - o.h - (o.offsetY ?? 0)
          return rectsOverlap(
            carHitbox.x,
            carHitbox.y,
            carHitbox.w,
            carHitbox.h,
            o.x,
            oy,
            o.w,
            o.h,
          )
        })

        if (collided) {
          crash()
        } else {
          const prevBest = s.best
          s.score += dt * 10 + s.speed * dt * 0.01
          s.best = Math.max(s.best, s.score)
          if (s.best > prevBest) writeBestScore(s.best)
        }
      }

      draw()
      rafRef.current = window.requestAnimationFrame(step)
    },
    [canSpawnNow, crash, draw, height, phase, releaseJumpCharge, running, width],
  )

  React.useEffect(() => {
    const s = stateRef.current
    s.best = readBestScore()
    s.groundY = height - PHYSICS_CONFIG.groundPadding
    s.car.y = s.groundY - s.car.h
    if (!s.scenery) {
      s.scenery = createScenery(s.seed, width, height)
    }

    rafRef.current = window.requestAnimationFrame(step)
    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current)
    }
  }, [height, step, width])

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!running) return
      if (event.code === 'Enter') {
        startGame()
      }
      if (event.code === 'Space') {
        if (event.repeat) return
        event.preventDefault()
        if (phase === 'playing') startJump(true)
        else startGame()
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        releaseJumpCharge()
        jumpAllowedRef.current = true
      }
    }

    const handleCanvasClick = (event: MouseEvent) => {
      if (!running) return
      const rect = canvasRef.current?.getBoundingClientRect()
      if (!rect) return
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top

      if (phase === 'playing') {
        startJump(false)
        return
      }

      const btn = uiRef.current.startBtn
      if (
        x >= btn.x &&
        x <= btn.x + btn.w &&
        y >= btn.y &&
        y <= btn.y + btn.h
      ) {
        startGame()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    const canvasEl = canvasRef.current
    if (canvasEl) {
      canvasEl.addEventListener('mousedown', handleCanvasClick)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      if (canvasEl) {
        canvasEl.removeEventListener('mousedown', handleCanvasClick)
      }
    }
  }, [phase, releaseJumpCharge, running, startGame, startJump])

  return (
    <Box
      sx={{
        position: 'relative',
        width,
        height,
      }}
    >
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{ width: '100%', height: '100%' }}
      />
    </Box>
  )
}

export default CarRunnerMiniGame
